/**
 * @description API services for payments and credit orders
 * @author Omar Sánchez Martinez
 * @since 2025-10-17
 */

import { apiMunzen } from '@/app/api/apiMunzen/apiMunzen';
import type { PaymentGatewayApiResponse, PaymentOrderResponse } from '../types/payment.interface';

/**
 * @author: Omar Sánchez Martinez
 * @since: 2025-10-17
 * @description: Create or update a pre-order for credit purchase
 * @param subscriptionPlanId - ID of the subscription plan to purchase
 * @param preOrderId - Optional pre-order ID for updating existing pre-order
 */
export const createPreOrderService = async (
  subscriptionPlanId: number,
  preOrderId?: string,
): Promise<PaymentGatewayApiResponse> => {
  const payload: {
    subscription_plan_id: number;
    pre_order_id?: string;
  } = {
    subscription_plan_id: subscriptionPlanId,
  };

  if (preOrderId) {
    payload.pre_order_id = preOrderId;
  }

  const response = await apiMunzen.post<PaymentGatewayApiResponse>(
    '/payments/credits/pre-order',
    payload,
  );
  return response.data;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 2025-10-17
 * @description: Fetch payment order details from payment gateway
 * @param paymentGatewayOrderId - Payment intent ID from Stripe
 */
export const fetchPaymentOrderService = async (
  paymentGatewayOrderId: string,
  options?: { signal?: AbortSignal },
): Promise<PaymentOrderResponse> => {
  const response = await apiMunzen.get<PaymentOrderResponse>(
    `/payments/credits/get-order-from-payment-gateway/${paymentGatewayOrderId}`,
    { signal: options?.signal },
  );
  return response.data;
};
